import React, { useMemo } from 'react';
import { Radio, Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useElasticsearchOptions } from '@client/hooks/useElasticsearchOptions';
import { useAutoSelectSingleOption } from '@client/feature/treatment/hooks/useAutoSelectSingleOption';

/**
 * SP-430 (R51/R52/R55/R56/R60/R61/R64): the Recherche rapide criteria 2-5 are
 * presented as radio buttons stacked vertically (single choice), in
 * alphabetical order, instead of a search dropdown.
 *
 * Options are fetched eagerly when the step becomes active (so the single-option
 * auto-selection of R12 can fire), through the same Elasticsearch autocomplete
 * the dropdown used — so the mandatory `is_fully_valid` base query and the
 * directional cascade facets (activeFacets) still apply. Counts are never shown.
 */
const ElasticsearchRadioGroup = ({
  document,
  field,
  valueField = 'id',
  labelField,
  value,
  onChange,
  activeFacets,
  sortBy = 'natural',
  sortOrder = 'asc',
  // SP-430 R65: when set (e.g. 'intervention_period.priority'), options are
  // ordered by this numeric source field (the Admin display order) instead of
  // alphabetically.
  sortSourceField = null,
  enabled = true,
}) => {
  const { t } = useTranslation('treatment-search');

  const { data, isLoading } = useElasticsearchOptions({
    document,
    field,
    valueField,
    labelField,
    pageSize: 100,
    sortBy,
    sortOrder,
    facets: activeFacets,
    includeSourceFields: sortSourceField ? [sortSourceField] : null,
    enabled,
  });

  const options = useMemo(() => {
    const flat = (data?.pages || []).flatMap((page) => page.options || []);
    if (!sortSourceField) return flat;
    // The autocomplete returns source as { '<path>': [value] }; sort by it.
    const rank = (opt) => {
      const raw = opt.source?.[sortSourceField];
      const num = Number(Array.isArray(raw) ? raw[0] : raw);
      return Number.isFinite(num) ? num : Infinity;
    };
    return [...flat].sort((a, b) => rank(a) - rank(b));
  }, [data, sortSourceField]);

  const selectedValue = useMemo(() => {
    if (!Array.isArray(value) || value.length === 0) return undefined;
    const match = options.find((opt) => value.includes(opt.value));
    return match ? match.value : value[0];
  }, [value, options]);

  // R12: if a single option is possible, select it (the parent's onChange
  // handles auto-progress / auto-launch, so no separate advance here).
  const selectSingleOption = (opt) => onChange(opt.value, { value: opt.value, label: opt.label });
  useAutoSelectSingleOption({
    options: options.map((opt) => ({ id: opt.value, label: opt.label })),
    isLoading,
    value,
    onSelect: (opt) => selectSingleOption({ value: opt.id, label: opt.label }),
    onAdvance: undefined,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spin tip={t('common.loading', 'Chargement...')} />
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('common.no_data', 'Aucune donnée')}
      />
    );
  }

  return (
    <div className="green-radio w-full sm:w-1/2">
      <Radio.Group
        value={selectedValue}
        onChange={(e) => {
          const opt = options.find((o) => o.value === e.target.value);
          if (opt) onChange(opt.value, { value: opt.value, label: opt.label });
        }}
        className="flex flex-col gap-1 max-h-[300px] overflow-y-auto"
      >
        {options.map((opt) => (
          <Radio key={opt.value} value={opt.value} className="py-1 text-xs sm:text-sm">
            <span className="text-xs sm:text-sm">{opt.label}</span>
          </Radio>
        ))}
      </Radio.Group>
    </div>
  );
};

export default ElasticsearchRadioGroup;
